import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MapPin, 
  GraduationCap, 
  Bus, 
  ShoppingCart, 
  Hospital, 
  Coffee,
  Fuel,
  Shield,
  Star,
  Clock,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Property {
  id: string;
  latitude?: number | null;
  longitude?: number | null;
  city: string | null;
  location: string;
  pincode?: string | null;
}

interface Amenity {
  id: string;
  name: string;
  type: string;
  distance: number;
  rating?: number;
  address: string;
  category: 'restaurant' | 'hospital' | 'shopping' | 'fuel' | 'entertainment';
}

interface School {
  id: string;
  name: string;
  type: 'Primary' | 'Secondary' | 'Higher Secondary' | 'University';
  rating?: number;
  distance: number;
  address: string;
  board?: string;
}

interface Transport {
  id: string;
  name: string;
  type: 'Metro' | 'Bus Stop' | 'Railway' | 'Airport';
  distance: number;
  lines?: string[];
  address: string;
}

interface SafetyInfo {
  crimeRate: 'Low' | 'Moderate' | 'High';
  policeStations: number;
  emergencyServices: number;
  lightingQuality: 'Poor' | 'Average' | 'Good' | 'Excellent';
  safetyScore: number;
}

interface NeighborhoodInsightsProps {
  property: Property;
}

export const NeighborhoodInsights: React.FC<NeighborhoodInsightsProps> = ({ property }) => {
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [transport, setTransport] = useState<Transport[]>([]);
  const [safetyInfo, setSafetyInfo] = useState<SafetyInfo | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchNeighborhoodData();
  }, [property]);

  const fetchNeighborhoodData = async () => {
    setLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('neighborhood-insights', {
        body: {
          latitude: property.latitude,
          longitude: property.longitude,
          location: property.location,
          city: property.city,
          pincode: property.pincode,
          propertyId: property.id
        }
      });

      if (error) {
        console.error('Error fetching neighborhood insights:', error);
        toast.error('Failed to load neighborhood insights');
        generateFallbackData();
        return;
      }

      if (data) {
        // Transform the API response to match our component structure
        const transformedAmenities = data.amenities?.map((place: any) => ({
          id: place.id,
          name: place.name,
          type: place.type,
          distance: place.distance,
          rating: place.rating,
          address: place.address,
          category: place.category
        })) || [];

        const transformedSchools = data.schools?.map((place: any) => ({
          id: place.id,
          name: place.name,
          type: place.category === 'school' ? 'Primary' : 'Secondary',
          rating: place.rating,
          distance: place.distance,
          address: place.address,
          board: 'Various'
        })) || [];

        const transformedTransport = data.transport?.map((place: any) => ({
          id: place.id,
          name: place.name,
          type: place.type.includes('subway') ? 'Metro' : 
                place.type.includes('train') ? 'Railway' : 'Bus Stop',
          distance: place.distance,
          address: place.address,
          lines: ['Check Locally'] // Would need additional API call for specific line info
        })) || [];

        setAmenities(transformedAmenities);
        setSchools(transformedSchools);
        setTransport(transformedTransport);
        setSafetyInfo(data.safetyInfo || null);
      } else {
        generateFallbackData();
      }
    } catch (error) {
      console.error('Error fetching neighborhood data:', error);
      toast.error('Failed to load neighborhood insights');
      generateFallbackData();
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackData = () => {
    // Fallback to mock data if API fails
    const mockAmenities: Amenity[] = [
      {
        id: '1',
        name: 'Local Shopping Center',
        type: 'Shopping Mall',
        distance: 1.2,
        rating: 4.0,
        address: `Near ${property.location}`,
        category: 'shopping'
      },
      {
        id: '2',
        name: 'Nearby Hospital',
        type: 'Hospital',
        distance: 0.8,
        rating: 4.2,
        address: `${property.city} Area`,
        category: 'hospital'
      },
      {
        id: '3',
        name: 'Local Cafe',
        type: 'Cafe',
        distance: 0.3,
        rating: 3.9,
        address: `${property.location}`,
        category: 'restaurant'
      }
    ];

    const mockSchools: School[] = [
      {
        id: '1',
        name: 'Local Primary School',
        type: 'Primary',
        rating: 4.0,
        distance: 0.5,
        address: `${property.location}`,
        board: 'State Board'
      }
    ];

    const mockTransport: Transport[] = [
      {
        id: '1',
        name: 'Bus Stop',
        type: 'Bus Stop',
        distance: 0.2,
        lines: ['Local Routes'],
        address: `Near ${property.location}`
      }
    ];

    const mockSafetyInfo: SafetyInfo = {
      crimeRate: 'Moderate',
      policeStations: 1,
      emergencyServices: 2,
      lightingQuality: 'Average',
      safetyScore: 7.0
    };

    setAmenities(mockAmenities);
    setSchools(mockSchools);
    setTransport(mockTransport);
    setSafetyInfo(mockSafetyInfo);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'restaurant':
        return <Coffee className="h-4 w-4" />;
      case 'hospital':
        return <Hospital className="h-4 w-4" />;
      case 'shopping':
        return <ShoppingCart className="h-4 w-4" />;
      case 'fuel':
        return <Fuel className="h-4 w-4" />;
      default:
        return <MapPin className="h-4 w-4" />;
    }
  };

  const getTransportIcon = (type: string) => {
    switch (type) {
      case 'Metro':
        return '🚇';
      case 'Railway':
        return '🚂';
      case 'Bus Stop':
        return '🚌';
      case 'Airport':
        return '✈️';
      default:
        return '🚌';
    }
  };

  const getSafetyColor = (rate: string) => {
    switch (rate) {
      case 'Low':
        return 'bg-green-100 text-green-800';
      case 'Moderate':
        return 'bg-yellow-100 text-yellow-800';
      case 'High':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Neighborhood Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-primary" />
          Neighborhood Insights
        </CardTitle>
        <p className="text-sm text-muted-foreground flex items-center gap-1">
          <span>Discover what's around this property in {property.location}, {property.city}</span>
          {(property.latitude && property.longitude) ? (
            <Badge variant="secondary" className="text-xs">Live Data</Badge>
          ) : (
            <Badge variant="outline" className="text-xs flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Estimated
            </Badge>
          )}
        </p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="amenities" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="amenities">Amenities</TabsTrigger>
            <TabsTrigger value="schools">Schools</TabsTrigger>
            <TabsTrigger value="transport">Transport</TabsTrigger>
            <TabsTrigger value="safety">Safety</TabsTrigger>
          </TabsList>

          <TabsContent value="amenities" className="space-y-4">
            <div className="grid gap-3">
              {amenities.slice(0, 3).map((amenity) => (
                <div key={amenity.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    {getCategoryIcon(amenity.category)}
                    <div>
                      <h4 className="font-medium">{amenity.name}</h4>
                      <p className="text-sm text-muted-foreground">{amenity.type}</p>
                      <p className="text-xs text-muted-foreground">{amenity.address}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm">
                      <Clock className="h-3 w-3" />
                      {amenity.distance} km
                    </div>
                    {amenity.rating && (
                      <div className="flex items-center gap-1 text-sm">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        {amenity.rating}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="schools" className="space-y-4">
            <div className="grid gap-3">
              {schools.slice(0, 3).map((school) => (
                <div key={school.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <GraduationCap className="h-4 w-4 text-blue-600" />
                    <div>
                      <h4 className="font-medium">{school.name}</h4>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Badge variant="outline" className="text-xs">{school.type}</Badge>
                        {school.board && <span>{school.board}</span>}
                      </div>
                      <p className="text-xs text-muted-foreground">{school.address}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm">
                      <Clock className="h-3 w-3" />
                      {school.distance} km
                    </div>
                    {school.rating && (
                      <div className="flex items-center gap-1 text-sm">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        {school.rating}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="transport" className="space-y-4">
            <div className="grid gap-3">
              {transport.slice(0, 3).map((stop) => (
                <div key={stop.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">{getTransportIcon(stop.type)}</span>
                    <div>
                      <h4 className="font-medium">{stop.name}</h4>
                      <Badge variant="outline" className="text-xs">{stop.type}</Badge>
                      {stop.lines && (
                        <div className="flex items-center gap-1 mt-1">
                          {stop.lines.map((line, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {line}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">{stop.address}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-sm">
                      <Clock className="h-3 w-3" />
                      {stop.distance} km
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="safety" className="space-y-4">
            {safetyInfo && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg bg-gradient-to-r from-green-50 to-blue-50">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Safety Score</span>
                      <Shield className="h-4 w-4 text-green-600" />
                    </div>
                    <div className="text-2xl font-bold text-green-600">
                      {safetyInfo.safetyScore}/10
                    </div>
                  </div>
                  
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Crime Rate</span>
                    </div>
                    <Badge className={getSafetyColor(safetyInfo.crimeRate)}>
                      {safetyInfo.crimeRate}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 border rounded-lg">
                    <div className="text-lg font-semibold">{safetyInfo.policeStations}</div>
                    <div className="text-xs text-muted-foreground">Police Stations</div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-lg font-semibold">{safetyInfo.emergencyServices}</div>
                    <div className="text-xs text-muted-foreground">Emergency Services</div>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <div className="text-lg font-semibold">{safetyInfo.lightingQuality}</div>
                    <div className="text-xs text-muted-foreground">Street Lighting</div>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Safety information is based on local crime statistics, emergency services availability, 
                    and infrastructure quality. Data is regularly updated from official sources.
                  </p>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};